type AnyRecord = Record<string, any>;

export type ResumeTemplateId = 'ats-optimized' | 'executive' | 'professional-classic';

type NormalizedExperience = {
  company: string;
  position: string;
  location: string;
  dateRange: string;
  bullets: string[];
  projects: string[];
  sortScore: number;
  originalIndex: number;
};

type NormalizedEducation = {
  institution: string;
  degree: string;
  location: string;
  dateRange: string;
  details: string[];
  sortScore: number;
  originalIndex: number;
};

type NormalizedReference = {
  name: string;
  position: string;
  company: string;
  email: string;
  phone: string;
};

type NormalizedContactItem = {
  label: string;
  url?: string;
  kind?: 'portfolio';
};

type NormalizedResume = {
  fullName: string;
  jobTitle: string;
  contactItems: NormalizedContactItem[];
  contactLine: string;
  summary: string;
  experiences: NormalizedExperience[];
  education: NormalizedEducation[];
  skills: string[];
  technicalSkills: string[];
  softSkills: string[];
  languages: string[];
  certifications: string[];
  awards: string[];
  projects: string[];
  publications: string[];
  additionalInfo: string[];
  references: NormalizedReference[];
  fileName: string;
};

const MONTHS: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

const MONTH_LABELS = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const PRESENT_SCORE = 999999;
const DEFAULT_NAME = 'FIRST LAST NAME';

// function cleanString(value: any): string {
//   if (value === null || value === undefined) return '';
//   if (typeof value === 'string') return value.replace(/\s+/g, ' ').trim();
//   if (typeof value === 'number') return String(value);
//   if (typeof value === 'boolean') return value ? 'Yes' : '';
//   if (value instanceof Date) {
//     if (Number.isNaN(value.getTime())) return '';
//     return `${MONTH_LABELS[value.getMonth() + 1]} ${value.getFullYear()}`;
//   }
//   if (Array.isArray(value)) return value.map(cleanString).filter(Boolean).join(', ');
//   if (typeof value === 'object') {
//     return cleanString(
//       value.fullName ||
//       value.name ||
//       value.title ||
//       value.jobTitle ||
//       value.position ||
//       value.company ||
//       value.companyName ||
//       value.value ||
//       value.label ||
//       value.description ||
//       value.text ||
//       value.detail ||
//       value.email ||
//       value.phone ||
//       value.phoneNumber ||
//       ''
//     );
//   }

//   return String(value).replace(/\s+/g, ' ').trim();
// }
function cleanString(value: any): string {
  if (value === null || value === undefined) return '';

  // Handle Primitives
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : '';

  // Handle Dates
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return `${MONTH_LABELS[value.getMonth() + 1]} ${value.getFullYear()}`;
  }

  // Handle Arrays
  if (Array.isArray(value)) return value.map(cleanString).filter(Boolean).join(', ');

  // Handle Objects (extracting common text properties recursively)
  if (typeof value === 'object') {
    return cleanString(
      value.fullName ||
      value.name ||
      value.title ||
      value.jobTitle ||
      value.position ||
      value.company ||
      value.companyName ||
      value.value ||
      value.label ||
      value.description ||
      value.text ||
      value.detail ||
      value.email ||
      value.phone ||
      value.phoneNumber ||
      ''
    );
  }

  // ---------------------------------------------------------
  // ASCII Normalization (Fixes PDF encoding/gibberish issues)
  // ---------------------------------------------------------
  
  // By this point, the value is either a string or an unhandled primitive that needs coercion
  const text = typeof value === 'string' ? value : String(value);

  // If the extractor accidentally passed raw PDF/source bytes, truncate at the first
  // If the extractor accidentally passed raw PDF/source bytes, truncate at the
  // first sign of PDF structure to avoid rendering binary gibberish in the output.
  // We look for common PDF tokens and return everything before the first match.
  const PDF_MARKERS = ['%PDF-', 'endobj', 'xref', 'trailer', 'obj <<', '/Type /Font', '/Font', '/Length', '/Filter', 'stream', 'BT', 'ET', 'Tf', 'Tj', 'TJ'];
  let earliestIndex = -1;
  for (const marker of PDF_MARKERS) {
    const idx = text.indexOf(marker);
    if (idx !== -1 && (earliestIndex === -1 || idx < earliestIndex)) earliestIndex = idx;
  }

  // If we found a clear structural marker, cut everything off before it.
  if (earliestIndex !== -1) {
    // For the generic 'stream' token be slightly conservative: ensure there is a
    // high proportion of non-printable characters following it (indicating embedded
    // binary) before truncating at 'stream'. For other structural markers, truncate
    // immediately.
    const streamIdx = text.indexOf('stream');
    if (earliestIndex === streamIdx) {
      const afterStream = text.slice(streamIdx + 'stream'.length, streamIdx + 'stream'.length + 200);
      const nonPrintable = (afterStream.match(/[^\x09\x0A\x0D\x20-\x7E]/g) || []).length;
      const ratio = nonPrintable / Math.max(1, afterStream.length);
      if (ratio > 0.02) return text.slice(0, streamIdx).replace(/\s+$/g, '').trim();
    } else {
      return text.slice(0, earliestIndex).replace(/\s+$/g, '').trim();
    }
  }

  return text
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")  // Convert smart single quotes & apostrophes to standard '
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')  // Convert smart double quotes to standard "
    .replace(/[\u2013\u2014]/g, '-')              // Convert en-dashes and em-dashes to standard hyphen
    .replace(/\u2026/g, '...')                    // Convert typography ellipses to three dots
    .replace(/[\u00A0\u202F\u2007\u2060]/g, ' ')  // Convert non-breaking and spacing characters to standard space
    .replace(/\s+/g, ' ')                         // Condense all remaining whitespace/line breaks into a single space
    .trim();
}
const RAW_EXTRACTED_TEXT_PATTERN = /(?:uploaded resume extracted text|first 100 words|_extractedtextpreview|_first100words|parser metadata|raw extracted text|HYPERLINK\b|mailto:|\\h|\\l|PROFESSIONAL SUMMARY\b|ADDITIONAL INFORMATION\b)/i;

function isLikelyRawExtractedText(value: any): boolean {
  if (typeof value !== 'string') return false;
  const rawText = value.trim();
  if (!rawText) return false;

  if (RAW_EXTRACTED_TEXT_PATTERN.test(rawText)) return true;

  // If text is short, it's unlikely to be raw extracted content
  if (rawText.length < 30) return false;

  // Heuristics for printable-but-gibberish content:
  const lettersDigits = (rawText.match(/[A-Za-z0-9]/g) || []).length;
  const nonAlphaNum = rawText.length - lettersDigits;
  const nonAlphaNumRatio = nonAlphaNum / Math.max(1, rawText.length);
  if (nonAlphaNumRatio > 0.35) return true;

  const punctuation = (rawText.match(/[!"#\$%&'()\*+,\-./:;<=>?@[\\\]^_`{|}~]/g) || []).length;
  const punctuationRatio = punctuation / Math.max(1, rawText.length);
  if (punctuationRatio > 0.25) return true;

  const tokens = rawText.split(/\s+/);
  const shortTokens = tokens.filter((t) => t.length <= 2).length;
  if (tokens.length > 20 && shortTokens / tokens.length > 0.6) return true;

  const uppercaseTokens = (rawText.match(/\b[A-Z]{4,}\b/g) || []).length;
  if (uppercaseTokens > 3) return true;

  if (/(?:endobj|xref|trailer|obj <<|\/Font|\/Length|stream|\bBT\b|\bET\b|\bTJ\b|\bTj\b)/i.test(rawText)) return true;

  // Detect PDF object patterns like "13 0 obj" or "end 13 0"
  if (/\b\d+\s+\d+\s+obj\b/i.test(rawText) || /\bend\s+\d+\s+\d+\b/i.test(rawText)) return true;

  // Few vowels relative to alpha-numeric can indicate encoded/binary noise
  const vowels = (rawText.match(/[aeiouAEIOU]/g) || []).length;
  if (rawText.length > 200 && vowels / Math.max(1, lettersDigits) < 0.12) return true;

  // Default: not raw extracted text
  return false;
}

function asArray(value: any): any[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

// function splitTextList(value: any, splitCommas = false): string[] {
//   if (!value) return [];

//   if (Array.isArray(value)) {
//     return value.flatMap((item) => splitTextList(item, splitCommas));
//   }

//   if (typeof value === 'object') {
//     return [cleanString(value)].filter(Boolean);
//   }

//   const text = cleanString(value);
//   if (!text) return [];

//   const bulletSeparators = /\s*(?:\u2022|\u00d8|Ø|·|•|\u00e2\u20ac\u00a2|\u00c3\u00a2\u00e2\u201a\u00ac\u00c2\u00a2)\s*/;
//   const separators = splitCommas
//     ? new RegExp(`\\r?\\n|[;|]|,(?=\\s*[A-Za-z0-9])|${bulletSeparators.source}`, 'g')
//     : new RegExp(`\\r?\\n|[;|]|${bulletSeparators.source}`, 'g');

//   return text
//     .split(separators)
//     .map((item) => item.replace(/^(?:[-*]|\u2022|\u00d8|Ø|·|•|\u00e2\u20ac\u00a2|\u00c3\u00a2\u00e2\u201a\u00ac\u00c2\u00a2)\s*/, '').trim())
//     .filter(Boolean);
// }
function splitTextList(value: any, splitCommas = false): string[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => splitTextList(item, splitCommas));
  }

  if (typeof value === 'object') {
    return [cleanString(value)].filter(Boolean);
  }

  // Convert to string without stripping the line breaks yet
  const rawText = String(value);
  if (!rawText.trim()) return [];

  const bulletSeparators = /\s*(?:\u2022|\u00d8|Ø|·|•|\u00e2\u20ac\u00a2|\u00c3\u00a2\u00e2\u201a\u00ac\u00c2\u00a2)\s*/;
  const separators = splitCommas
    ? new RegExp(`\\r?\\n|[;|]|,(?=\\s*[A-Za-z0-9])|${bulletSeparators.source}`, 'g')
    : new RegExp(`\\r?\\n|[;|]|${bulletSeparators.source}`, 'g');

  return rawText
    .split(separators)
    .map((item) => {
      // Remove leading bullet characters
      const unbulleted = item.replace(/^(?:[-*]|\u2022|\u00d8|Ø|·|•|\u00e2\u20ac\u00a2|\u00c3\u00a2\u00e2\u201a\u00ac\u00c2\u00a2)\s*/, '').trim();
      // Apply cleanString to the individual isolated bullet to normalize its internal spacing
      return cleanString(unbulleted);
    })
    .filter(Boolean);
}

function sanitizeAdditionalInfoItem(item: string): string {
  if (!item) return '';
  const text = cleanString(item);
  if (!text) return '';

  // If heuristics detect the whole item is raw extracted text, drop it
  if (isLikelyRawExtractedText(text)) return '';

  // Attempt to truncate any trailing PDF/source noise while preserving leading readable text.
  function truncateAtNoise(t: string): string {
    const markers = ['%PDF-', 'endobj', 'xref', 'trailer', 'obj <<', '/Font', '/Length', '/Filter', 'stream', 'BT', 'ET', 'Tj', 'TJ'];
    // earliest marker index
    let idx = -1;
    for (const m of markers) {
      const i = t.indexOf(m);
      if (i !== -1 && (idx === -1 || i < idx)) idx = i;
    }
    // also PDF object patterns
    const objMatch = t.match(/\b\d+\s+\d+\s+obj\b/i);
    if (objMatch && (idx === -1 || (objMatch.index ?? -1) < idx)) idx = objMatch.index ?? idx;
    const endObjMatch = t.match(/\bend\s+\d+\s+\d+\b/i);
    if (endObjMatch && (idx === -1 || (endObjMatch.index ?? -1) < idx)) idx = endObjMatch.index ?? idx;

    if (idx !== -1) {
      const prefix = t.slice(0, idx).trim();
      return prefix.length >= 10 ? prefix : '';
    }

    // Sliding window scan for high-noise regions and truncate at their start
    const windowSize = 120;
    for (let i = 0; i < t.length; i += 20) {
      const window = t.slice(i, i + windowSize);
      const nonPrintable = (window.match(/[^\x09\x0A\x0D\x20-\x7E]/g) || []).length;
      const punctuation = (window.match(/[!"#\$%&'()\*+,\-./:;<=>?@[\\\]^_`{|}~]/g) || []).length;
      if (nonPrintable / Math.max(1, window.length) > 0.12 || punctuation / Math.max(1, window.length) > 0.4) {
        const prefix = t.slice(0, i).trim();
        return prefix.length >= 10 ? prefix : '';
      }
    }

    // Fallback: if the string contains many short tokens after some point, cut there
    const tokens = t.split(/\s+/);
    let shortRunStart = -1;
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].length <= 2) {
        shortRunStart = shortRunStart === -1 ? i : shortRunStart;
      } else {
        if (shortRunStart !== -1) {
          const run = tokens.slice(shortRunStart, i);
          if (run.length > 8 && run.filter((tk) => tk.length <= 2).length / run.length > 0.6) {
            const prefix = tokens.slice(0, shortRunStart).join(' ').trim();
            return prefix.length >= 10 ? prefix : '';
          }
          shortRunStart = -1;
        }
      }
    }

    return t.trim();
  }

  const truncated = truncateAtNoise(text);
  if (!truncated) return '';

  // Final safety: ensure truncated piece isn't itself flagged as raw extracted text
  if (isLikelyRawExtractedText(truncated)) return '';

  // Keep up to the first few plausible sentences to avoid trailing binary-like noise.
  const sentences = truncated.match(/[^.!?]+[.!?]?/g) || [truncated];
  const kept: string[] = [];
  for (const s of sentences) {
    const sTrim = s.trim();
    if (sTrim.length < 10) continue;
    const letters = (sTrim.match(/[A-Za-z]/g) || []).length;
    const vowels = (sTrim.match(/[aeiouAEIOU]/g) || []).length;
    const vowelRatio = letters ? vowels / letters : 0;
    const words = sTrim.split(/\s+/).filter(Boolean).length;
    if (words >= 3 && vowelRatio > 0.12) kept.push(sTrim);
    if (kept.length >= 3) break;
  }

  let final = kept.join(' ').trim();
  if (!final) {
    // Fallback: take a safe prefix and ensure it looks textual
    final = truncated.slice(0, 240).trim();
    const letters = (final.match(/[A-Za-z]/g) || []).length;
    const vowels = (final.match(/[aeiouAEIOU]/g) || []).length;
    if (letters === 0 || vowels / Math.max(1, letters) < 0.1) return '';
  }

  return final;
}


function unique(items: string[]): string[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function escapeHTML(value: any): string {
  return cleanString(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function paragraph(value: string): string {
  return escapeHTML(value);
}

function renderList(items: string[], className = ''): string {
  if (!items.length) return '';
  const classAttr = className ? ` class="${className}"` : '';
  return `<ul${classAttr}>${items.map((item) => `<li>${paragraph(item)}</li>`).join('')}</ul>`;
}

function renderInlineList(items: string[]): string {
  if (!items.length) return '';
  return `<p class="inline-list">${items.map(paragraph).join(', ')}</p>`;
}

function renderSection(title: string, body: string, className = ''): string {
  if (!body.trim()) return '';
  const classAttr = className ? ` ${className}` : '';
  return `<section class="resume-section${classAttr}"><h2>${escapeHTML(title)}</h2>${body}</section>`;
}

function parseDateScore(value: any): number {
  const text = cleanString(value).toLowerCase();
  if (!text) return 0;
  if (/\b(present|current|now|ongoing)\b/.test(text)) return PRESENT_SCORE;

  const isoMatches = [...text.matchAll(/\b((?:19|20)\d{2})[-/](0?[1-9]|1[0-2])\b/g)];
  if (isoMatches.length) {
    const latest = isoMatches[isoMatches.length - 1];
    const year = Number(latest[1]);
    const month = Number(latest[2]);
    return year * 12 + month;
  }

  const monthYearMatches = [...text.matchAll(/\b([a-z]+)\.?\s+((?:19|20)\d{2})\b/g)];
  if (monthYearMatches.length) {
    const latest = monthYearMatches[monthYearMatches.length - 1];
    const month = MONTHS[latest[1]] || 1;
    return Number(latest[2]) * 12 + month;
  }

  const yearMatches = [...text.matchAll(/\b((?:19|20)\d{2})\b/g)];
  if (yearMatches.length) return Number(yearMatches[yearMatches.length - 1][1]) * 12 + 12;

  const date = new Date(cleanString(value));
  if (!Number.isNaN(date.getTime())) return date.getFullYear() * 12 + date.getMonth() + 1;

  return 0;
}

function formatSingleDate(value: any): string {
  const text = cleanString(value);
  if (!text) return '';
  if (/\b(present|current|now|ongoing)\b/i.test(text)) return 'Present';

  const parsedDate = new Date(text);
  if (!Number.isNaN(parsedDate.getTime()) && /\b(?:19|20)\d{2}\b/.test(text)) {
    return `${MONTH_LABELS[parsedDate.getMonth() + 1]} ${parsedDate.getFullYear()}`;
  }

  const isoMatch = text.match(/^\s*((?:19|20)\d{2})[-/](0?[1-9]|1[0-2])\s*$/);
  if (isoMatch) {
    return `${MONTH_LABELS[Number(isoMatch[2])]} ${isoMatch[1]}`;
  }

  const yearOnly = text.match(/^\s*((?:19|20)\d{2})\s*$/);
  if (yearOnly) return yearOnly[1];

  return text;
}

function formatDateRange(item: AnyRecord): string {
  if (item.datesDisabled) return '';

  const duration = cleanString(item.duration || item.dateRange || item.dates || item.period || item.years);
  if (duration) return duration.replace(/\s+[-\u2013\u2014]\s+/g, ' - ');

  const start = formatSingleDate(
    item.startDate ||
    item.start ||
    item.from ||
    [item.startMonth, item.startYear].filter(Boolean).join(' ') ||
    item.startYear
  );
  const end = formatSingleDate(
    item.current || item.isCurrent || item.present || item.currentlyWorking || item.presentlyWorking
      ? 'Present'
      : item.endDate ||
        item.end ||
        item.to ||
        [item.endMonth, item.endYear].filter(Boolean).join(' ') ||
        item.endYear
  );

  if (start && end) return `${start} - ${end}`;
  return start || end;
}

function sortReverseChronological<T extends { sortScore: number; originalIndex: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    if (b.sortScore !== a.sortScore) return b.sortScore - a.sortScore;
    return a.originalIndex - b.originalIndex;
  });
}

function normalizeBullets(item: AnyRecord): string[] {
  return unique([
    ...splitTextList(item.description),
    ...splitTextList(item.descriptions),
    ...splitTextList(item.responsibilities),
    ...splitTextList(item.tasks),
    ...splitTextList(item.task),
    ...splitTextList(item.achievements),
    ...splitTextList(item.keyAchievements),
    ...splitTextList(item.accomplishments),
    ...splitTextList(item.duties),
    ...splitTextList(item.bullets),
  ])
    .map((bullet) => bullet.replace(/\*\*/g, '').replace(/\*/g, '').trim())
    .filter(Boolean)
    .slice(0, 4);
}

function titleCaseName(value: string): string {
  return cleanString(value)
    .toLowerCase()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function normalizeUrl(value: any): string {
  const text = cleanString(value);
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  return `https://${text.replace(/^\/+/, '')}`;
}

function formatLinkedInLabel(value: any): string {
  const text = cleanString(value);
  if (!text) return '';

  try {
    const url = new URL(normalizeUrl(text));
    const path = url.pathname.replace(/\/+$/, '');
    if (/linkedin\.com$/i.test(url.hostname.replace(/^www\./, '')) && path) {
      return `linkedin.com${path}`;
    }
  } catch {
    // Fall through to text cleanup.
  }

  return text
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');
}

function labeledDetails(label: string, value: any): string[] {
  return splitTextList(value).map((item) => (
    new RegExp(`^${label}:`, 'i').test(item) ? item : `${label}: ${item}`
  ));
}

function normalizeProjects(value: any): string[] {
  return unique(asArray(value).flatMap((project) => {
    if (!project) return [];
    if (typeof project !== 'object') {
      if (isLikelyRawExtractedText(project)) return [];
      return splitTextList(project).map((item) => item.replace(/^project experience:\s*/i, '').trim());
    }

    const title = cleanString(project.name || project.title);
    const description = unique([
      ...splitTextList(project.description),
      ...splitTextList(project.summary),
      ...splitTextList(project.details),
    ]).join('; ');
    const technologies = splitTextList(project.technologies || project.tools, true).join(', ');
    const link = cleanString(project.link || project.url);
    const parts = [
      title,
      description,
      technologies ? `Technologies: ${technologies}` : '',
      link,
    ].filter(Boolean);

    return parts.length ? [parts.join(' - ')] : [];
  }).map((project) => project.replace(/^project experience:\s*/i, '').trim()).filter(Boolean));
}

function normalizeBlobText(value: any): string {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => String(item).trim())
      .join('\n\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/[ \t]+/g, ' ')
      .replace(/ *\n */g, '\n')
      .trim();
  }

  if (typeof value !== 'string') return '';

  return value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .trim();
}

function splitExperienceBlob(value: any): string[] {
  const text = normalizeBlobText(value);
  if (!text) return [];

  const blocks = text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (blocks.length > 1) return blocks;

  const dateRangeSplit = text.split(/(?=(?:\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?|(?:19|20)\d{2})\b\s*[-–—]\s*(?:Present|(?:19|20)\d{2})\b))/i)
    .map((part) => part.trim())
    .filter(Boolean);

  if (dateRangeSplit.length > 1) return dateRangeSplit;

  const lines = text
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length > 1 && lines.some((line) => /\b(?:19|20)\d{2}\b/.test(line))) {
    return [lines.join('\n')];
  }

  return [text];
}

function normalizeExperience(value: any): NormalizedExperience[] {
  return sortReverseChronological(
    asArray(value)
      .filter(Boolean)
      .filter((item) => {
        if (typeof item === 'string') return !isLikelyRawExtractedText(item);
        if (typeof item === 'object' && item.description && isLikelyRawExtractedText(item.description)) return false;
        return true;
      })
      .flatMap((item) => {
        if (typeof item === 'object') {
          const hasCompanyOrPosition = cleanString(
            item.company || item.companyName || item.employer || item.organization || item.client ||
            item.position || item.title || item.jobTitle || item.role || item.job_title
          );
          const descriptions = asArray(item.description || item.descriptions).filter(Boolean);

          if (!hasCompanyOrPosition && descriptions.length === 1 && typeof descriptions[0] === 'string') {
            const entries = splitExperienceBlob(descriptions[0]);
            if (entries.length > 1) {
              return entries.map((entry) => ({ description: entry }));
            }
          }
        }

        return [item];
      })
      .map((item, index) => {
        const exp = typeof item === 'object' ? item : { description: item };
        const dateRange = formatDateRange(exp);
        const sortScore = Math.max(
          parseDateScore(exp.endDate || exp.end || exp.to || exp.endYear || [exp.endMonth, exp.endYear].filter(Boolean).join(' ') || dateRange),
          parseDateScore(exp.startDate || exp.start || exp.from || exp.startYear || [exp.startMonth, exp.startYear].filter(Boolean).join(' '))
        );

        return {
          company: cleanString(exp.company || exp.companyName || exp.employer || exp.organization || exp.client),
          position: cleanString(exp.position || exp.title || exp.jobTitle || exp.role || exp.job_title),
          location: cleanString(exp.location || exp.city || exp.country),
          dateRange,
          bullets: normalizeBullets(exp),
          projects: normalizeProjects(exp.projects || exp.projectExperience || exp.selectedProjects),
          sortScore,
          originalIndex: index,
        };
      })
      .filter((item) => item.company || item.position || item.bullets.length)
  );
}

function normalizeEducation(value: any): NormalizedEducation[] {
  return sortReverseChronological(
    asArray(value)
      .filter(Boolean)
      .filter((item) => typeof item !== 'string' || !isLikelyRawExtractedText(item))
      .map((item, index) => {
        const edu = typeof item === 'object' ? item : { degree: item };
        const degreeParts = [
          cleanString(edu.degree || edu.qualification || edu.program),
          cleanString(edu.major || edu.field || edu.fieldOfStudy || edu.course),
        ].filter(Boolean);
        const dateRange = formatDateRange(edu);
        const sortScore = Math.max(
          parseDateScore(edu.endDate || edu.end || edu.endYear || [edu.endMonth, edu.endYear].filter(Boolean).join(' ') || dateRange),
          parseDateScore(edu.startDate || edu.start || edu.startYear || [edu.startMonth, edu.startYear].filter(Boolean).join(' '))
        );
        const grade = cleanString(edu.grade || edu.grades || edu.gpa);
        const details = unique([
          ...splitTextList(grade ? `Grade: ${grade}` : ''),
          ...labeledDetails('Award', edu.awards),
          ...labeledDetails('Honor', edu.honors),
          ...labeledDetails('Project', edu.projects || edu.project),
          ...labeledDetails('Publication', edu.publications || edu.publication),
        ]);

        return {
          institution: cleanString(edu.institution || edu.schoolName || edu.school || edu.university || edu.college),
          degree: degreeParts.join(' in '),
          location: cleanString(edu.location || edu.city || edu.country),
          dateRange,
          details,
          sortScore,
          originalIndex: index,
        };
      })
      .filter((item) => item.institution || item.degree)
  );
}

function normalizeReferences(value: any): NormalizedReference[] {
  return asArray(value)
    .flatMap((item) => {
      if (!item) return [];
      if (typeof item === 'string') return splitTextList(item);
      return [item];
    })
    .map((item) => {
      if (typeof item !== 'object') {
        const text = cleanString(item);
        if (!text || isReferencePlaceholder(text)) return null;

        return {
          name: titleCaseName(text),
          position: '',
          company: '',
          email: '',
          phone: '',
        };
      }

      const title = [
        cleanString(item.position || item.title || item.role || item.relationship),
        cleanString(item.company || item.organization),
      ].filter(Boolean).join(', ');
      const name = cleanString(item.name || item.fullName || item.refereeName || item.referenceName);
      const fallbackText = cleanString(item);

      return {
        name: titleCaseName(name || (!title && !isReferencePlaceholder(fallbackText) ? fallbackText : '')),
        position: cleanString(item.position || item.title || item.role || item.relationship),
        company: cleanString(item.company || item.organization),
        email: cleanString(item.email),
        phone: cleanString(item.phone || item.mobile || item.phoneNumber || item.contactNumber),
      };
    })
    .filter((item): item is NormalizedReference => Boolean(item && (item.name || item.position || item.company || item.email || item.phone)));
}

function isReferencePlaceholder(value: string): boolean {
  return /\b(references?|referees?)\b.*\b(available|upon request)\b/i.test(value) ||
    /\bavailable upon request\b/i.test(value);
}

function normalizeLanguageList(value: any): string[] {
  return unique(asArray(value).flatMap((item) => {
    if (!item) return [];
    if (typeof item !== 'object') return splitTextList(item, true);

    const name = cleanString(item.name || item.language || item.title);
    const proficiency = cleanString(item.proficiency || item.level);
    return [name && proficiency ? `${name} (${proficiency})` : name || proficiency].filter(Boolean);
  }));
}

function normalizeCertificationList(value: any): string[] {
  return unique(asArray(value).flatMap((item) => {
    if (!item) return [];
    if (typeof item !== 'object') return splitTextList(item);

    const name = cleanString(item.name || item.title || item.certification || item.certificate);
    const issuer = cleanString(item.issuer || item.organization);
    const date = [cleanString(item.month), cleanString(item.year)].filter(Boolean).join(' ') ||
      cleanString(item.date || item.issueDate || item.completedAt);
    const parts = [name, issuer, date].filter(Boolean);
    return parts.length ? [parts.join(' - ')] : [];
  }));
}

function normalizeSimpleList(value: any, splitCommas = false): string[] {
  return unique(asArray(value).flatMap((item) => {
    if (!item) return [];
    if (typeof item !== 'object') {
      const list = splitTextList(item, splitCommas);
      return list.filter((entry) => !isLikelyRawExtractedText(entry));
    }
    const cleaned = cleanString(item);
    return cleaned && !isLikelyRawExtractedText(cleaned) ? [cleaned] : [];
  }));
}

function isDuplicateAdditionalItem(item: string, coreText: string): boolean {
  if (!item) return true;
  const low = item.toLowerCase().replace(/\s+/g, ' ').trim();
  if (!low) return true;
  if (low.length < 8) return true; // too short to be useful
  // If item is mostly contact info or a URL, keep it only if unique
  if (/^https?:\/\//i.test(item) || /github\.com|linkedin\.com|mailto:|@/.test(item)) {
    // let URL/email pass unless it already appears in coreText
    return coreText.toLowerCase().includes(low);
  }

  // If the item is contained verbatim in the core resume text, consider it duplicate
  if (coreText.toLowerCase().includes(low)) return true;

  // If the item shares a very high overlap of words with coreText, treat as duplicate
  const itemWords = Array.from(new Set(low.split(/\s+/)));
  const coreWords = new Set(coreText.toLowerCase().split(/\s+/));
  let matches = 0;
  for (const w of itemWords) if (coreWords.has(w)) matches++;
  if (matches / Math.max(1, itemWords.length) > 0.6) return true;

  return false;
}

function combinePhone(prefix: any, phone: any): string {
  const cleanedPhone = cleanString(phone);
  const cleanedPrefix = cleanString(prefix);
  if (!cleanedPhone) return '';
  if (!cleanedPrefix || cleanedPhone.startsWith(cleanedPrefix)) return cleanedPhone;
  return `${cleanedPrefix} ${cleanedPhone}`;
}

function buildFallbackSummary(
  jobTitle: string,
  experiences: NormalizedExperience[],
  technicalSkills: string[],
  softSkills: string[],
  education: NormalizedEducation[]
): string {
  const strongestSkills = [...technicalSkills, ...softSkills].slice(0, 4);
  if (!jobTitle && !experiences.length && !strongestSkills.length && !education.length) return '';

  const role = jobTitle || experiences[0]?.position || 'Professional';
  const years = inferExperienceYears(experiences);
  const coreArea = strongestSkills.length
    ? strongestSkills.slice(0, 3).join(', ')
    : experiences[0]?.position || education[0]?.degree || 'professional delivery';
  const proof = experiences.some((exp) => exp.bullets.some((bullet) => /\d/.test(bullet)))
    ? 'Proven track record of delivering measurable results while improving quality, collaboration, and execution.'
    : 'Proven track record of translating responsibilities into clear outcomes through reliable execution and collaborative problem-solving.';
  const objective = role !== 'Professional'
    ? `Focused on contributing ${role.toLowerCase()} expertise to teams that value quality, usability, and continuous improvement.`
    : 'Focused on contributing practical expertise to teams that value quality, accountability, and continuous improvement.';

  return `Detail-oriented ${role} ${years ? `with ${years}+ years of experience` : 'with hands-on experience'} in ${coreArea}. ${proof} ${objective}`
    .replace(/\s+\./g, '.')
    .trim();
}

function inferExperienceYears(experiences: NormalizedExperience[]): number | null {
  const years = experiences.flatMap((exp) => {
    const foundYears = [...exp.dateRange.matchAll(/\b((?:19|20)\d{2})\b/g)].map((match) => Number(match[1]));
    return /\bPresent\b/i.test(exp.dateRange) ? [...foundYears, new Date().getFullYear()] : foundYears;
  });
  if (years.length < 2) return null;
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  const inferred = maxYear - minYear;
  return inferred > 0 && inferred < 60 ? inferred : null;
}

const CONTACT_SEPARATOR = ' \u2022 ';
const PORTFOLIO_LINK_HEX = '#60a5fa';
const PORTFOLIO_LINK_DOCX_COLOR = '60A5FA';
const PORTFOLIO_LINK_RGB = { r: 96, g: 165, b: 250 };

export function normalizeResumeData(resumeData: AnyRecord = {}): NormalizedResume {
  const root = typeof resumeData === 'string'
    ? (() => {
        try {
          return JSON.parse(resumeData);
        } catch {
          return { additionalInfo: resumeData };
        }
      })()
    : resumeData || {};
  const personalInfo = root.personalInfo || root.contactInfo || root.contact || root.basics || {};
  const firstName = cleanString(personalInfo.firstName || root.firstName);
  const middleName = cleanString(personalInfo.middleName || root.middleName);
  const lastName = cleanString(personalInfo.lastName || root.lastName);
  const fullName = cleanString(
    personalInfo.fullName ||
    personalInfo.name ||
    root.fullName ||
    root.name ||
    [firstName, middleName, lastName].filter(Boolean).join(' ')
  ) || DEFAULT_NAME;
  const jobTitle = cleanString(
    personalInfo.jobTitle ||
    personalInfo.title ||
    root.jobTitle ||
    root.title ||
    root.currentTitle ||
    root.role
  );
  const phone = combinePhone(
    personalInfo.dialCode || personalInfo.countryCode || root.dialCode || root.countryCode,
    personalInfo.mobile ||
      personalInfo.phone ||
      personalInfo.phoneNumber ||
      root.phone ||
      root.mobile ||
      root.phoneNumber ||
      root.contactNumber ||
      root.telephone
  );
  const location = cleanString(
    personalInfo.location ||
    root.location ||
    [personalInfo.city || root.city, personalInfo.country || root.country].filter(Boolean).join(', ')
  );
  const email = cleanString(personalInfo.email || root.email);
  const linkedInUrl = cleanString(personalInfo.linkedin || personalInfo.linkedinUrl || root.linkedin || root.linkedinUrl);
  const portfolioUrl = cleanString(personalInfo.portfolio || personalInfo.portfolioUrl || root.portfolio || root.portfolioUrl || root.website);
  const contactItems: NormalizedContactItem[] = [
    location ? { label: location } : null,
    phone ? { label: phone } : null,
    email ? { label: email, url: `mailto:${email}` } : null,
    linkedInUrl ? { label: formatLinkedInLabel(linkedInUrl), url: normalizeUrl(linkedInUrl) } : null,
    portfolioUrl ? { label: 'Portfolio', url: normalizeUrl(portfolioUrl), kind: 'portfolio' } : null,
  ].filter(Boolean) as NormalizedContactItem[];
  const contactLine = contactItems.map((item) => item.label).join(CONTACT_SEPARATOR);

  const technicalSkills = unique([
    ...splitTextList(root.skills?.technical, true),
    ...splitTextList(root.skills?.technicalSkills, true),
    ...splitTextList(root.technicalSkills, true),
    ...splitTextList(root.hardSkills, true),
    ...splitTextList(root.skills?.tools, true),
  ]);
  const softSkills = unique([
    ...splitTextList(root.skills?.soft, true),
    ...splitTextList(root.skills?.softSkills, true),
    ...splitTextList(root.softSkills, true),
  ]);
  const skills = unique([
    ...splitTextList(root.skills, true),
    ...technicalSkills,
    ...softSkills,
    ...splitTextList(root.hardSkills, true),
    ...splitTextList(root.coreCompetencies, true),
    ...splitTextList(root.competencies, true),
    ...splitTextList(root.skillSet, true),
  ]);
  const languages = unique([
    ...normalizeLanguageList(root.languages),
    ...normalizeLanguageList(root.languageSkills),
  ]);
  const certifications = unique([
    ...normalizeCertificationList(root.certifications),
    ...normalizeCertificationList(root.certificates),
    ...normalizeCertificationList(root.credentials),
  ]);
  const awards = unique([
    ...normalizeSimpleList(root.awards),
    ...normalizeSimpleList(root.honors),
  ]);
  const projects = normalizeProjects(root.projects || root.personalProjects || root.projectExperience);
  const publications = normalizeSimpleList(root.publications || root.publication);
  const rawAdditional = [
    ...splitTextList(root.additionalInfo || root.additionalInformation),
    ...normalizeSimpleList(root.interests, true),
  ].filter(Boolean);

  let additionalInfo: string[] = [];
  const experiences = normalizeExperience(
    root.workExperience ||
    root.experience ||
    root.professionalExperience ||
    root.employmentHistory ||
    root.workHistory ||
    root.careerHistory ||
    root.jobs
  );
  const education = normalizeEducation(root.education || root.educationList || root.studies || root.academic || root.academicBackground || root.educationHistory || root.qualifications);
  const rawSummary = cleanString(root.summary || root.professionalSummary || root.profileSummary || root.careerObjective || root.objective);
  const summary = !isLikelyRawExtractedText(rawSummary)
    ? rawSummary || buildFallbackSummary(jobTitle, experiences, technicalSkills, softSkills, education)
    : buildFallbackSummary(jobTitle, experiences, technicalSkills, softSkills, education);
  const fileName = `${fullName.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Resume'}_Resume`;

  // Compute final additionalInfo now that experiences and education are available
  try {
    const coreTextParts = [
      fullName,
      jobTitle,
      contactLine,
      rawSummary,
      experiences.map((e) => `${e.company} ${e.position} ${e.dateRange} ${e.bullets.join(' ')}`).join(' '),
      education.map((ed) => `${ed.institution} ${ed.degree} ${ed.details.join(' ')}`).join(' '),
      skills.join(' '),
      projects.join(' '),
    ].filter(Boolean).join('\n');

    additionalInfo = unique(rawAdditional
      .map((it) => sanitizeAdditionalInfoItem(it))
      .filter(Boolean)
      .filter((it) => !isDuplicateAdditionalItem(it, coreTextParts))
    ).slice(0, 10);
  } catch {
    additionalInfo = rawAdditional.map(sanitizeAdditionalInfoItem).filter(Boolean).slice(0, 10);
  }

  return {
    fullName,
    jobTitle,
    contactItems,
    contactLine,
    summary,
    experiences,
    education,
    skills,
    technicalSkills,
    softSkills,
    languages,
    certifications,
    awards,
    projects,
    publications,
    additionalInfo,
    references: normalizeReferences(root.references || root.referees || root.professionalReferences),
    fileName,
  };
}

function renderHeader(data: NormalizedResume, variant: ResumeTemplateId): string {
  const title = data.jobTitle ? `<p class="job-title">${paragraph(data.jobTitle)}</p>` : '';
  const contact = data.contactItems.length
    ? `<p class="contact">${data.contactItems.map((item) => (
        item.url
          ? `<a class="${item.kind === 'portfolio' ? 'portfolio-link' : 'contact-link'}" href="${escapeHTML(item.url)}">${paragraph(item.label)}</a>`
          : paragraph(item.label)
      )).join(' <span class="contact-separator">&#8226;</span> ')}</p>`
    : '';
  const headerClass = variant === 'ats-optimized' ? 'ats-header centered-header' : 'centered-header';

  return `
    <header class="${headerClass}">
      <h1>${paragraph(data.fullName)}</h1>
      ${title}
      ${contact}
    </header>
  `;
}

function renderExperienceItems(data: NormalizedResume, includeProjects = false): string {
  return data.experiences
    .map((exp) => {
      const companyLine = [exp.company, exp.location].filter(Boolean).join(', ');
      const titleLine = exp.position ? `<p class="role">${paragraph(exp.position)}</p>` : '';
      const projectBlock = includeProjects && exp.projects.length
        ? `<p class="minor-heading">Selected Project Experience</p>${renderList(exp.projects, 'compact-list')}`
        : '';

      return `
        <article class="entry">
          ${(companyLine || exp.dateRange) ? `
            <div class="entry-heading-row">
              <p class="entry-heading">${paragraph(companyLine)}</p>
              ${exp.dateRange ? `<p class="entry-date">${paragraph(exp.dateRange)}</p>` : ''}
            </div>
          ` : ''}
          ${titleLine}
          ${renderList(exp.bullets, 'achievement-list')}
          ${projectBlock}
        </article>
      `;
    })
    .join('');
}

function renderEducationItems(data: NormalizedResume): string {
  return data.education
    .map((edu) => {
      const institutionLine = [edu.institution, edu.location].filter(Boolean).join(', ');
      const degree = edu.degree ? `<p class="role">${paragraph(edu.degree)}</p>` : '';
      const details = edu.details.length ? renderList(edu.details, 'compact-list') : '';

      return `
        <article class="entry">
          ${(institutionLine || edu.dateRange) ? `
            <div class="entry-heading-row">
              <p class="entry-heading">${paragraph(institutionLine)}</p>
              ${edu.dateRange ? `<p class="entry-date">${paragraph(edu.dateRange)}</p>` : ''}
            </div>
          ` : ''}
          ${degree}
          ${details}
        </article>
      `;
    })
    .join('');
}

function renderSkillsBody(data: NormalizedResume): string {
  const rows = [
    data.technicalSkills.length ? `<p><strong>Technical Skills:</strong> ${data.technicalSkills.map(paragraph).join(', ')}</p>` : '',
    data.softSkills.length ? `<p><strong>Soft Skills:</strong> ${data.softSkills.map(paragraph).join(', ')}</p>` : '',
  ].filter(Boolean);

  if (rows.length) return rows.join('');
  return renderInlineList(data.skills);
}

function renderAdditionalInfo(data: NormalizedResume): string {
  const safeAdditional = data.additionalInfo
    .map(sanitizeAdditionalInfoItem)
    .filter(Boolean)
    .slice(0, 10);

  const rows = [
    data.languages.length ? `<p><strong>Languages:</strong> ${data.languages.map(paragraph).join(', ')}</p>` : '',
    data.certifications.length ? `<p><strong>Certifications:</strong> ${data.certifications.map(paragraph).join(', ')}</p>` : '',
    data.awards.length ? `<p><strong>Awards:</strong> ${data.awards.map(paragraph).join(', ')}</p>` : '',
    safeAdditional.length ? `<p><strong>Additional:</strong> ${safeAdditional.map(paragraph).join(', ')}</p>` : '',
  ].filter(Boolean);

  return rows.join('');
}

function renderProjectsSection(data: NormalizedResume): string {
  return data.projects.length ? renderList(data.projects, 'compact-list') : '';
}

function renderPublicationsSection(data: NormalizedResume): string {
  return data.publications.length ? renderList(data.publications, 'compact-list') : '';
}

function renderReferencesBody(data: NormalizedResume, includeFallback = false): string {
  if (!data.references.length) {
    return includeFallback ? '<p>Available upon request.</p>' : '';
  }

  return data.references
    .map((item) => {
      const contact = item.email || item.phone;
      const positionCompany = [item.position, item.company].filter(Boolean).join(', ').toUpperCase();
      return `
        <div class="reference-entry">
          <p class="reference-name">${paragraph(item.name)}${contact ? ` - ${paragraph(contact)}` : ''}</p>
          ${positionCompany ? `<p class="reference-title"><strong>${paragraph(positionCompany)}</strong></p>` : ''}
        </div>
      `;
    })
    .join('');
}

function renderATSOptimized(data: NormalizedResume): string {
  return `
    ${renderHeader(data, 'ats-optimized')}
    ${renderSection('SUMMARY', data.summary ? `<p>${paragraph(data.summary)}</p>` : '')}
    ${renderSection('PROFESSIONAL EXPERIENCE', renderExperienceItems(data))}
    ${renderSection('EDUCATION', renderEducationItems(data))}
    ${renderSection('SKILLS', renderSkillsBody(data))}
    ${renderSection('PROJECTS', renderProjectsSection(data))}
    ${renderSection('PUBLICATIONS', renderPublicationsSection(data))}
    ${renderSection('ADDITIONAL INFORMATION', renderAdditionalInfo(data))}
    ${renderSection('REFERENCES', renderReferencesBody(data, true))}
  `;
}

function renderExecutive(data: NormalizedResume): string {
  return `
    ${renderHeader(data, 'executive')}
    ${renderSection('OBJECTIVE', data.summary ? `<p>${paragraph(data.summary)}</p>` : '')}
    ${renderSection('PROFESSIONAL EXPERIENCE', renderExperienceItems(data, true))}
    ${renderSection('EDUCATION', renderEducationItems(data))}
    ${renderSection('SKILLS', renderSkillsBody(data))}
    ${renderSection('PROJECTS', renderProjectsSection(data))}
    ${renderSection('PUBLICATIONS', renderPublicationsSection(data))}
    ${renderSection('ADDITIONAL INFORMATION', renderAdditionalInfo(data))}
    ${renderSection('REFERENCES', renderReferencesBody(data, true))}
  `;
}

function renderProfessionalClassic(data: NormalizedResume): string {
  return `
    ${renderHeader(data, 'professional-classic')}
    ${renderSection('SUMMARY', data.summary ? `<p>${paragraph(data.summary)}</p>` : '')}
    ${renderSection('PROFESSIONAL EXPERIENCE', renderExperienceItems(data))}
    ${renderSection('EDUCATION', renderEducationItems(data))}
    ${renderSection('SKILLS', renderSkillsBody(data))}
    ${renderSection('PROJECTS', renderProjectsSection(data))}
    ${renderSection('PUBLICATIONS', renderPublicationsSection(data))}
    ${renderSection('ADDITIONAL INFORMATION', renderAdditionalInfo(data))}
    ${renderSection('REFERENCES', renderReferencesBody(data, true))}
  `;
}

function getTemplateBody(templateId: ResumeTemplateId, data: NormalizedResume): string {
  if (templateId === 'ats-optimized') return renderATSOptimized(data);
  if (templateId === 'executive') return renderExecutive(data);
  return renderProfessionalClassic(data);
}

const baseCSS = `
  @page {
    size: Letter;
    margin: 0.5in 0.55in;
  }

  body.ats-optimized *,
  body.executive *,
  body.professional-classic *,
  .ats-optimized *,
  .executive *,
  .professional-classic * {
    box-sizing: border-box;
  }

  body.ats-optimized,
  body.executive,
  body.professional-classic,
  .ats-optimized,
  .executive,
  .professional-classic {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: #111111;
    font-family: Arial, Helvetica, sans-serif;
    line-height: 1.34;
  }

  body.ats-optimized .resume-document,
  body.executive .resume-document,
  body.professional-classic .resume-document,
  .ats-optimized .resume-document,
  .executive .resume-document,
  .professional-classic .resume-document {
    width: 8.5in;
    min-height: 11in;
    height: auto;
    margin: 0 auto;
    padding: 0.5in 0.55in;
    background: #ffffff;
    color: #111111;
  }

  body.ats-optimized header,
  body.executive header,
  body.professional-classic header,
  .ats-optimized header,
  .executive header,
  .professional-classic header {
    margin-bottom: 16px;
  }

  body.ats-optimized h1,
  body.ats-optimized h2,
  body.ats-optimized p,
  body.ats-optimized ul,
  body.executive h1,
  body.executive h2,
  body.executive p,
  body.executive ul,
  body.professional-classic h1,
  body.professional-classic h2,
  body.professional-classic p,
  body.professional-classic ul,
  .ats-optimized h1,
  .ats-optimized h2,
  .ats-optimized p,
  .ats-optimized ul,
  .executive h1,
  .executive h2,
  .executive p,
  .executive ul,
  .professional-classic h1,
  .professional-classic h2,
  .professional-classic p,
  .professional-classic ul {
    margin: 0;
  }

  body.ats-optimized h1,
  body.executive h1,
  body.professional-classic h1,
  .ats-optimized h1,
  .executive h1,
  .professional-classic h1 {
    font-size: 22pt;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  body.ats-optimized h2,
  body.executive h2,
  body.professional-classic h2,
  .ats-optimized h2,
  .executive h2,
  .professional-classic h2 {
    margin-top: 15px;
    padding-bottom: 4px;
    border-bottom: 1px solid #111111;
    font-size: 10.5pt;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  body.ats-optimized p,
  body.ats-optimized li,
  body.executive p,
  body.executive li,
  body.professional-classic p,
  body.professional-classic li,
  .ats-optimized p,
  .ats-optimized li,
  .executive p,
  .executive li,
  .professional-classic p,
  .professional-classic li {
    font-size: 10.4pt;
  }

  body.ats-optimized ul,
  body.executive ul,
  body.professional-classic ul,
  .ats-optimized ul,
  .executive ul,
  .professional-classic ul {
    padding-left: 18px;
  }

  body.ats-optimized li,
  body.executive li,
  body.professional-classic li,
  .ats-optimized li,
  .executive li,
  .professional-classic li {
    margin: 2px 0;
    padding-left: 1px;
  }

  body.ats-optimized .job-title,
  body.executive .job-title,
  body.professional-classic .job-title,
  .ats-optimized .job-title,
  .executive .job-title,
  .professional-classic .job-title {
    margin-top: 3px;
    font-size: 11.2pt;
    font-weight: 600;
  }

  body.ats-optimized .contact,
  body.executive .contact,
  body.professional-classic .contact,
  .ats-optimized .contact,
  .executive .contact,
  .professional-classic .contact {
    margin-top: 4px;
    font-size: 9.6pt;
  }

  body.ats-optimized .contact a,
  body.executive .contact a,
  body.professional-classic .contact a,
  .ats-optimized .contact a,
  .executive .contact a,
  .professional-classic .contact a {
    color: #111111;
    text-decoration: none;
  }

  body.ats-optimized .contact a.portfolio-link,
  body.executive .contact a.portfolio-link,
  body.professional-classic .contact a.portfolio-link,
  .ats-optimized .contact a.portfolio-link,
  .executive .contact a.portfolio-link,
  .professional-classic .contact a.portfolio-link {
    color: ${PORTFOLIO_LINK_HEX};
    text-decoration: underline;
  }

  body.ats-optimized .centered-header,
  body.executive .centered-header,
  body.professional-classic .centered-header,
  .ats-optimized .centered-header,
  .executive .centered-header,
  .professional-classic .centered-header {
    text-align: center;
  }

  body.ats-optimized .ats-header,
  .ats-optimized .ats-header {
    text-align: center;
  }

  body.ats-optimized .entry,
  body.executive .entry,
  body.professional-classic .entry,
  .ats-optimized .entry,
  .executive .entry,
  .professional-classic .entry {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  body.ats-optimized .resume-section,
  body.executive .resume-section,
  body.professional-classic .resume-section,
  .ats-optimized .resume-section,
  .executive .resume-section,
  .professional-classic .resume-section {
    margin-top: 20px;
    break-inside: auto;
    page-break-inside: auto;
  }

  body.ats-optimized .resume-section:first-of-type,
  body.executive .resume-section:first-of-type,
  body.professional-classic .resume-section:first-of-type,
  .ats-optimized .resume-section:first-of-type,
  .executive .resume-section:first-of-type,
  .professional-classic .resume-section:first-of-type {
    margin-top: 0;
  }

  body.ats-optimized .entry,
  body.executive .entry,
  body.professional-classic .entry,
  .ats-optimized .entry,
  .executive .entry,
  .professional-classic .entry {
    margin-bottom: 9px;
  }

  body.ats-optimized .entry-heading,
  body.executive .entry-heading,
  body.professional-classic .entry-heading,
  .ats-optimized .entry-heading,
  .executive .entry-heading,
  .professional-classic .entry-heading {
    font-weight: 700;
  }

  body.ats-optimized .entry-heading-row,
  body.executive .entry-heading-row,
  body.professional-classic .entry-heading-row,
  .ats-optimized .entry-heading-row,
  .executive .entry-heading-row,
  .professional-classic .entry-heading-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: baseline;
  }

  body.ats-optimized .entry-date,
  body.executive .entry-date,
  body.professional-classic .entry-date,
  .ats-optimized .entry-date,
  .executive .entry-date,
  .professional-classic .entry-date {
    flex: 0 0 auto;
    text-align: right;
    white-space: nowrap;
  }

  body.ats-optimized .role,
  body.executive .role,
  body.professional-classic .role,
  .ats-optimized .role,
  .executive .role,
  .professional-classic .role {
    margin-top: 1px;
    font-style: italic;
  }

  body.ats-optimized .achievement-list,
  body.ats-optimized .compact-list,
  body.executive .achievement-list,
  body.executive .compact-list,
  body.professional-classic .achievement-list,
  body.professional-classic .compact-list,
  .ats-optimized .achievement-list,
  .ats-optimized .compact-list,
  .executive .achievement-list,
  .executive .compact-list,
  .professional-classic .achievement-list,
  .professional-classic .compact-list {
    margin-top: 4px;
  }

  body.ats-optimized .achievement-list li + li,
  body.executive .achievement-list li + li,
  body.professional-classic .achievement-list li + li,
  .ats-optimized .achievement-list li + li,
  .executive .achievement-list li + li,
  .professional-classic .achievement-list li + li {
    margin-top: 10px;
  }

  body.ats-optimized .inline-list,
  body.executive .inline-list,
  body.professional-classic .inline-list,
  .ats-optimized .inline-list,
  .executive .inline-list,
  .professional-classic .inline-list {
    margin-top: 2px;
  }

  body.ats-optimized .minor-heading,
  body.executive .minor-heading,
  body.professional-classic .minor-heading,
  .ats-optimized .minor-heading,
  .executive .minor-heading,
  .professional-classic .minor-heading {
    margin-top: 6px;
    font-weight: 700;
  }

  body.ats-optimized .reference-entry,
  body.executive .reference-entry,
  body.professional-classic .reference-entry,
  .ats-optimized .reference-entry,
  .executive .reference-entry,
  .professional-classic .reference-entry {
    margin-bottom: 7px;
  }

  body.ats-optimized .reference-title,
  body.executive .reference-title,
  body.professional-classic .reference-title,
  .ats-optimized .reference-title,
  .executive .reference-title,
  .professional-classic .reference-title {
    margin-top: 1px;
  }

  body.ats-optimized strong,
  body.executive strong,
  body.professional-classic strong,
  .ats-optimized strong,
  .executive strong,
  .professional-classic strong {
    font-weight: 700;
  }
`;

const atsCSS = `
  body.ats-optimized .resume-document,
  .ats-optimized .resume-document {
    font-family: Arial, Helvetica, sans-serif;
  }

  body.ats-optimized h1,
  .ats-optimized h1 {
    font-size: 21pt;
  }

  body.ats-optimized h2,
  .ats-optimized h2 {
    margin-top: 0;
  }
`;

const executiveCSS = `
  body.executive .resume-document,
  .executive .resume-document {
    padding-left: 0.62in;
    padding-right: 0.62in;
    font-family: Calibri, Arial, Helvetica, sans-serif;
  }

  body.executive h1,
  .executive h1 {
    font-size: 23pt;
  }

  body.executive h2,
  .executive h2 {
    margin-top: 0;
    border-bottom-width: 1.3px;
    font-size: 10.8pt;
  }

  body.executive .job-title,
  .executive .job-title {
    font-size: 11.6pt;
  }
`;

const professionalClassicCSS = `
  body.professional-classic .resume-document,
  .professional-classic .resume-document {
    padding-left: 0.58in;
    padding-right: 0.58in;
    font-family: Georgia, "Times New Roman", serif;
  }

  body.professional-classic h1,
  .professional-classic h1 {
    font-size: 22pt;
  }

  body.professional-classic h2,
  .professional-classic h2 {
    font-size: 10.6pt;
  }

  body.professional-classic .job-title,
  body.professional-classic .contact,
  body.professional-classic p,
  body.professional-classic li,
  .professional-classic .job-title,
  .professional-classic .contact,
  .professional-classic p,
  .professional-classic li {
    font-family: Arial, Helvetica, sans-serif;
  }
`;

function getCSS(templateId: ResumeTemplateId): string {
  const variantCSS = templateId === 'ats-optimized'
    ? atsCSS
    : templateId === 'executive'
      ? executiveCSS
      : professionalClassicCSS;

  return `${baseCSS}\n${variantCSS}`;
}

export function generateTemplateHTML(templateId: ResumeTemplateId, resumeData: AnyRecord): string {
  const data = normalizeResumeData(resumeData);
  const body = getTemplateBody(templateId, data);

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <title>${escapeHTML(data.fileName)}</title>
    <style>${getCSS(templateId)}</style>
  </head>
  <body class="${templateId}">
    <main class="resume-document">
      ${body}
    </main>
  </body>
</html>`;
}

function generateTemplateFragment(templateId: ResumeTemplateId, resumeData: AnyRecord): string {
  const data = normalizeResumeData(resumeData);
  return `<style>${getCSS(templateId)}</style><div class="${templateId}"><main class="resume-document">${getTemplateBody(templateId, data)}</main></div>`;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function getFileName(resumeData: AnyRecord, extension: 'docx' | 'pdf') {
  return `${normalizeResumeData(resumeData).fileName}.${extension}`;
}

function toBlob(value: any, type: string): Blob {
  if (value instanceof Blob) return value;
  return new Blob([value], { type });
}

export async function downloadTemplateDOCX(templateId: ResumeTemplateId, resumeData: AnyRecord): Promise<void> {
  try {
    const { default: htmlToDocx } = await import('html-to-docx');
    const html = generateTemplateHTML(templateId, resumeData);
    const docx = await htmlToDocx(html, undefined, {
      pageSize: {
        width: '8.5in',
        height: '11in',
      },
      margins: {
        top: '0.5in',
        right: '0.55in',
        bottom: '0.5in',
        left: '0.55in',
      },
      font: 'Calibri',
      fontSize: '11pt',
    });

    downloadBlob(
      toBlob(docx, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'),
      getFileName(resumeData, 'docx')
    );
  } catch (error) {
    console.warn('HTML to DOCX conversion failed, using structured DOCX fallback.', error);
    await downloadTemplateDOCXFallback(templateId, resumeData);
  }
}

async function downloadTemplateDOCXFallback(templateId: ResumeTemplateId, resumeData: AnyRecord): Promise<void> {
  const docxModule = await import('docx') as any;
  const {
    AlignmentType,
    BorderStyle,
    Document,
    Packer,
    Paragraph,
    TextRun,
  } = docxModule;
  const data = normalizeResumeData(resumeData);
  const centeredHeader = true;
  const children: any[] = [];

  const paragraphOptions = (spacingAfter = 80) => ({
    spacing: { after: spacingAfter },
  });
  const line = (
    text: string,
    options: {
      bold?: boolean;
      italics?: boolean;
      size?: number;
      alignment?: any;
      spacingAfter?: number;
    } = {}
  ) => new Paragraph({
    ...paragraphOptions(options.spacingAfter ?? 70),
    alignment: options.alignment,
    children: [
      new TextRun({
        text,
        bold: options.bold,
        italics: options.italics,
        size: options.size ?? 21,
      }),
    ],
  });
  const heading = (title: string) => new Paragraph({
    spacing: { before: 280, after: 130 },
    border: {
      bottom: {
        color: '111111',
        size: 6,
        style: BorderStyle.SINGLE,
      },
    },
    children: [
      new TextRun({
        text: title,
        bold: true,
        size: 22,
      }),
    ],
  });
  const bullet = (text: string, spacingAfter = 45) => new Paragraph({
    spacing: { after: spacingAfter },
    bullet: { level: 0 },
    children: [
      new TextRun({
        text,
        size: 21,
      }),
    ],
  });
  const addHeader = () => {
    const alignment = centeredHeader ? AlignmentType.CENTER : AlignmentType.LEFT;
    children.push(line(data.fullName.toUpperCase(), { bold: true, size: 34, alignment, spacingAfter: 60 }));
    if (data.jobTitle) children.push(line(data.jobTitle, { bold: true, size: 22, alignment, spacingAfter: 60 }));
    if (data.contactItems.length) {
      const runs = data.contactItems.flatMap((item, index) => {
        const itemRun = new TextRun({
          text: item.label,
          size: 19,
          color: item.kind === 'portfolio' ? PORTFOLIO_LINK_DOCX_COLOR : undefined,
          underline: item.kind === 'portfolio' ? { type: 'single' } : undefined,
        });
        const separatorRun = index < data.contactItems.length - 1
          ? new TextRun({ text: CONTACT_SEPARATOR, size: 19 })
          : null;
        return separatorRun ? [itemRun, separatorRun] : [itemRun];
      });

      children.push(new Paragraph({
        ...paragraphOptions(150),
        alignment,
        children: runs,
      }));
    }
  };
  const addSummary = (title: string) => {
    if (!data.summary) return;
    children.push(heading(title));
    children.push(line(data.summary, { size: 21, spacingAfter: 120 }));
  };
  const addExperience = (includeProjects = false) => {
    if (!data.experiences.length) return;
    children.push(heading('PROFESSIONAL EXPERIENCE'));
    data.experiences.forEach((exp) => {
      const companyLine = [exp.company, exp.location].filter(Boolean).join(', ');
      if (companyLine) children.push(line(companyLine, { bold: true, spacingAfter: 35 }));
      if (exp.dateRange) children.push(line(exp.dateRange, { spacingAfter: 35 }));
      if (exp.position) children.push(line(exp.position, { italics: true, spacingAfter: 35 }));
      exp.bullets.forEach((item) => children.push(bullet(item, 110)));
      if (includeProjects && exp.projects.length) {
        children.push(line('Selected Project Experience', { bold: true, spacingAfter: 35 }));
        exp.projects.forEach((item) => children.push(bullet(item)));
      }
    });
  };
  const addEducation = () => {
    if (!data.education.length) return;
    children.push(heading('EDUCATION'));
    data.education.forEach((edu) => {
      const institutionLine = [edu.institution, edu.location].filter(Boolean).join(', ');
      if (institutionLine) children.push(line(institutionLine, { bold: true, spacingAfter: 35 }));
      if (edu.dateRange) children.push(line(edu.dateRange, { spacingAfter: 35 }));
      if (edu.degree) children.push(line(edu.degree, { italics: true, spacingAfter: 35 }));
      edu.details.forEach((item) => children.push(bullet(item)));
    });
  };
  const addInlineSection = (title: string, items: string[]) => {
    if (!items.length) return;
    children.push(heading(title));
    children.push(line(items.join(', '), { spacingAfter: 110 }));
  };
  const addAdditionalInfo = (source: NormalizedResume = data) => {
    const rows = [
      source.skills.length ? `Technical Skills: ${source.skills.join(', ')}` : '',
      source.languages.length ? `Languages: ${source.languages.join(', ')}` : '',
      source.certifications.length ? `Certifications: ${source.certifications.join(', ')}` : '',
      source.awards.length ? `Awards: ${source.awards.join(', ')}` : '',
      source.additionalInfo.length ? `Additional: ${source.additionalInfo.join(', ')}` : '',
    ].filter(Boolean);

    if (!rows.length) return;
    children.push(heading('ADDITIONAL INFORMATION'));
    rows.forEach((item) => children.push(line(item, { spacingAfter: 55 })));
  };
  const addProjects = () => {
    if (!data.projects.length) return;
    children.push(heading('PROJECTS'));
    data.projects.forEach((item) => children.push(bullet(item)));
  };
  const addSkills = () => {
    if (!data.technicalSkills.length && !data.softSkills.length && !data.skills.length) return;
    children.push(heading('SKILLS'));
    if (data.technicalSkills.length) children.push(line(`Technical Skills: ${data.technicalSkills.join(', ')}`, { spacingAfter: 55 }));
    if (data.softSkills.length) children.push(line(`Soft Skills: ${data.softSkills.join(', ')}`, { spacingAfter: 55 }));
    if (!data.technicalSkills.length && !data.softSkills.length && data.skills.length) {
      children.push(line(data.skills.join(', '), { spacingAfter: 55 }));
    }
  };
  const addPublications = () => {
    if (!data.publications.length) return;
    children.push(heading('PUBLICATIONS'));
    data.publications.forEach((item) => children.push(bullet(item)));
  };
  const addReferences = (includeFallback = false) => {
    if (!data.references.length && !includeFallback) return;
    children.push(heading('REFERENCES'));
    if (!data.references.length) {
      children.push(line('Available upon request.'));
      return;
    }

    data.references.forEach((item) => {
      const contact = item.email || item.phone;
      children.push(line(`${item.name}${contact ? ` - ${contact}` : ''}`, { spacingAfter: 25 }));
      const positionCompany = [item.position, item.company].filter(Boolean).join(', ').toUpperCase();
      if (positionCompany) children.push(line(positionCompany, { bold: true, spacingAfter: 55 }));
    });
  };

  addHeader();

  if (templateId === 'executive') {
    addSummary('OBJECTIVE');
    addExperience(true);
    addEducation();
    addSkills();
    addProjects();
    addPublications();
    addAdditionalInfo();
    addReferences(true);
  } else if (templateId === 'ats-optimized') {
    addSummary('SUMMARY');
    addExperience();
    addEducation();
    addSkills();
    addProjects();
    addPublications();
    addAdditionalInfo();
    addReferences(true);
  } else {
    addSummary('SUMMARY');
    addExperience();
    addEducation();
    addSkills();
    addProjects();
    addPublications();
    addAdditionalInfo();
    addReferences(true);
  }

  const document = new Document({
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 720, right: 792, bottom: 720, left: 792 },
        },
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(document);
  downloadBlob(blob, getFileName(resumeData, 'docx'));
}

// Detect and reject resume data containing raw PDF/binary markers
function validateResumeDataForPDF(data: any): void {
  const binaryMarkers = ['%PDF-', 'stream', 'endobj', 'endstream'];
  const jsonStr = JSON.stringify(data);
  
  for (const marker of binaryMarkers) {
    if (jsonStr.indexOf(marker) !== -1 && /[^\x20-\x7E\x0A\x0D\t]/.test(jsonStr)) {
      throw new Error(
        `Resume data contains potential binary/PDF markers. This may indicate corrupted extracted text. ` +
        `Please re-upload the resume or manually enter the information.`
      );
    }
  }
}

export async function downloadTemplatePDF(templateId: ResumeTemplateId, resumeData: AnyRecord): Promise<void> {
  // Validate input data before PDF generation
  try {
    validateResumeDataForPDF(resumeData);
  } catch (validationErr) {
    console.error('Resume data validation failed:', validationErr);
    throw validationErr;
  }

  const jspdfModule = await import('jspdf');
  const JsPDFConstructor = (jspdfModule as any).jsPDF || (jspdfModule as any).default;
  
  // Initialize jsPDF with explicit UTF-8 encoding
  const doc = new JsPDFConstructor({ 
    unit: 'pt', 
    format: 'letter', 
    orientation: 'portrait',
    compress: true  // Enable compression to reduce PDF size and improve stability
  });
  
  const data = normalizeResumeData(resumeData);

  const pageWidth = 612;
  const pageHeight = 792;
  const margin = templateId === 'executive' ? 44 : 40;
  const contentWidth = pageWidth - margin * 2;
  const headerAlign = 'center';
  // Use Courier instead of Times for more reliable UTF-8 support
  const bodyFont = 'courier';
  const headerFont = 'courier';
  let y = margin;

  const setFont = (font = bodyFont, style: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal', size = 10.4) => {
    doc.setFont(font, style);
    doc.setFontSize(size);
    doc.setTextColor(17, 17, 17);
  };

  const ensureSpace = (height = 24) => {
    if (y + height > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeWrapped = (
    text: string,
    options: {
      x?: number;
      width?: number;
      size?: number;
      font?: string;
      style?: 'normal' | 'bold' | 'italic' | 'bolditalic';
      align?: 'left' | 'center' | 'right';
      lineHeight?: number;
      spacingAfter?: number;
      indent?: number;
    } = {}
  ) => {
    let cleaned = cleanString(text);
    if (!cleaned) return;

    // Defensive: Strip any remaining PDF/binary markers that slipped through
    const pdfIndex = cleaned.indexOf('%PDF-');
    if (pdfIndex !== -1) {
      cleaned = cleaned.slice(0, pdfIndex).trim();
    }
    const streamIndex = cleaned.indexOf('stream');
    if (streamIndex !== -1) {
      const nonPrintable = (cleaned.match(/[^\x09\x0A\x0D\x20-\x7E]/g) || []).length;
      if (nonPrintable / cleaned.length > 0.05) {
        cleaned = cleaned.slice(0, streamIndex).trim();
      }
    }
    
    if (!cleaned) return;

    const size = options.size ?? 10.4;
    const lineHeight = options.lineHeight ?? size * 1.24;
    const indent = options.indent ?? 0;
    const width = options.width ?? contentWidth - indent;
    const x = options.x ?? margin + indent;
    
    try {
      const lines = doc.splitTextToSize(cleaned, width) as string[];
      lines.forEach((line) => {
        ensureSpace(lineHeight + (options.spacingAfter ?? 0));
        setFont(options.font ?? bodyFont, options.style ?? 'normal', size);
        doc.text(line, x, y, { align: options.align ?? 'left' });
        y += lineHeight;
      });
    } catch (err) {
      console.error('PDF text rendering error:', err, 'text:', cleaned);
      // Silently skip corrupted lines to prevent PDF generation failure
    }

    y += options.spacingAfter ?? 3;
  };

  const heading = (title: string) => {
    ensureSpace(26);
    y += 12;
    setFont('helvetica', 'bold', templateId === 'executive' ? 10.8 : 10.5);
    doc.text(title, margin, y);
    y += 4;
    doc.setLineWidth(templateId === 'executive' ? 0.8 : 0.6);
    doc.line(margin, y, pageWidth - margin, y);
    y += 13;
  };

  const bullet = (text: string, spacingAfter = 1.5) => {
    writeWrapped(`- ${text}`, {
      indent: 12,
      width: contentWidth - 12,
      spacingAfter,
    });
  };

  const entryHeading = (text: string) => writeWrapped(text, {
    style: 'bold',
    spacingAfter: 0.5,
  });

  const roleLine = (text: string) => writeWrapped(text, {
    style: 'italic',
    spacingAfter: 1,
  });

  const writePairRow = (left: string, right = '', options: { bold?: boolean; spacingAfter?: number } = {}) => {
    if (!left && !right) return;
    ensureSpace(18);
    const rightWidth = right ? doc.getTextWidth(right) + 8 : 0;
    const leftWidth = Math.max(120, contentWidth - rightWidth - 14);
    const leftLines = doc.splitTextToSize(left || '', leftWidth) as string[];

    setFont(bodyFont, options.bold ? 'bold' : 'normal', 10.4);
    leftLines.forEach((line, index) => {
      ensureSpace(13);
      doc.text(line, margin, y);
      if (index === 0 && right) {
        doc.text(right, pageWidth - margin, y, { align: 'right' });
      }
      y += 12.5;
    });
    y += options.spacingAfter ?? 1;
  };

  const writeContactItems = () => {
    if (!data.contactItems.length) return;
    const separator = CONTACT_SEPARATOR;
    const labels = data.contactItems.map((item) => item.label);
    const fullText = labels.join(separator);
    setFont(bodyFont, 'normal', 9.6);

    const fullWidth = doc.getTextWidth(fullText);
    if (fullWidth > contentWidth) {
      writeWrapped(fullText, {
        x: pageWidth / 2,
        width: contentWidth,
        font: bodyFont,
        size: 9.6,
        align: 'center',
        lineHeight: 11.5,
        spacingAfter: 8,
      });
      return;
    }

    let x = pageWidth / 2 - fullWidth / 2;
    ensureSpace(14);
    data.contactItems.forEach((item, index) => {
      setFont(bodyFont, 'normal', 9.6);
      if (item.kind === 'portfolio') {
        doc.setTextColor(PORTFOLIO_LINK_RGB.r, PORTFOLIO_LINK_RGB.g, PORTFOLIO_LINK_RGB.b);
      }
      if (item.url && typeof (doc as any).textWithLink === 'function') {
        (doc as any).textWithLink(item.label, x, y, { url: item.url });
      } else {
        doc.text(item.label, x, y);
      }
      if (item.kind === 'portfolio') {
        const underlineY = y + 1.6;
        doc.setLineWidth(0.4);
        doc.setDrawColor(PORTFOLIO_LINK_RGB.r, PORTFOLIO_LINK_RGB.g, PORTFOLIO_LINK_RGB.b);
        doc.line(x, underlineY, x + doc.getTextWidth(item.label), underlineY);
        doc.setDrawColor(17, 17, 17);
        doc.setTextColor(17, 17, 17);
      }
      x += doc.getTextWidth(item.label);
      if (index < data.contactItems.length - 1) {
        doc.text(separator, x, y);
        x += doc.getTextWidth(separator);
      }
    });
    y += 20;
  };

  const addHeader = () => {
    const x = headerAlign === 'center' ? pageWidth / 2 : margin;
    writeWrapped(data.fullName.toUpperCase(), {
      x,
      width: contentWidth,
      font: headerFont,
      style: 'bold',
      size: templateId === 'executive' ? 23 : 22,
      align: headerAlign,
      lineHeight: templateId === 'executive' ? 25 : 24,
      spacingAfter: 1,
    });
    if (data.jobTitle) {
      writeWrapped(data.jobTitle, {
        x,
        width: contentWidth,
        font: bodyFont,
        style: 'bold',
        size: templateId === 'executive' ? 11.6 : 11.2,
        align: headerAlign,
        lineHeight: 13,
        spacingAfter: 1,
      });
    }
    if (data.contactLine) {
      writeContactItems();
    } else {
      y += 8;
    }
  };

  const addSummary = (title: string) => {
    if (!data.summary) return;
    heading(title);
    writeWrapped(data.summary, { spacingAfter: 4 });
  };

  const addExperience = (includeProjects = false) => {
    if (!data.experiences.length) return;
    heading('PROFESSIONAL EXPERIENCE');
    data.experiences.forEach((exp) => {
      ensureSpace(42);
      const companyLine = [exp.company, exp.location].filter(Boolean).join(', ');
      writePairRow(companyLine, exp.dateRange, { bold: true, spacingAfter: 0.5 });
      if (exp.position) roleLine(exp.position);
      exp.bullets.forEach((item) => bullet(item, 7.5));
      if (includeProjects && exp.projects.length) {
        writeWrapped('Selected Project Experience', { style: 'bold', spacingAfter: 0 });
        exp.projects.forEach(bullet);
      }
      y += 4;
    });
  };

  const addEducation = () => {
    if (!data.education.length) return;
    heading('EDUCATION');
    data.education.forEach((edu) => {
      ensureSpace(34);
      const institutionLine = [edu.institution, edu.location].filter(Boolean).join(', ');
      writePairRow(institutionLine, edu.dateRange, { bold: true, spacingAfter: 0.5 });
      if (edu.degree) roleLine(edu.degree);
      edu.details.forEach(bullet);
      y += 4;
    });
  };

  const addInlineSection = (title: string, items: string[]) => {
    if (!items.length) return;
    heading(title);
    writeWrapped(items.join(', '), { spacingAfter: 4 });
  };

  const addProjects = () => {
    if (!data.projects.length) return;
    heading('PROJECTS');
    data.projects.forEach(bullet);
  };

  const addSkills = () => {
    if (!data.technicalSkills.length && !data.softSkills.length && !data.skills.length) return;
    heading('SKILLS');
    if (data.technicalSkills.length) writeWrapped(`Technical Skills: ${data.technicalSkills.join(', ')}`, { spacingAfter: 2 });
    if (data.softSkills.length) writeWrapped(`Soft Skills: ${data.softSkills.join(', ')}`, { spacingAfter: 2 });
    if (!data.technicalSkills.length && !data.softSkills.length && data.skills.length) {
      writeWrapped(data.skills.join(', '), { spacingAfter: 2 });
    }
  };

  const addPublications = () => {
    if (!data.publications.length) return;
    heading('PUBLICATIONS');
    data.publications.forEach(bullet);
  };

  const addAdditionalInfo = (source: NormalizedResume = data) => {
    const rows = [
      source.languages.length ? `Languages: ${source.languages.join(', ')}` : '',
      source.certifications.length ? `Certifications: ${source.certifications.join(', ')}` : '',
      source.awards.length ? `Awards: ${source.awards.join(', ')}` : '',
      source.additionalInfo.length ? `Additional: ${source.additionalInfo.join(', ')}` : '',
    ].filter(Boolean);

    if (!rows.length) return;
    heading('ADDITIONAL INFORMATION');
    rows.forEach((row) => writeWrapped(row, { spacingAfter: 2 }));
  };

  const addReferences = (includeFallback = false) => {
    if (!data.references.length && !includeFallback) return;
    heading('REFERENCES');
    if (!data.references.length) {
      writeWrapped('Available upon request.', { spacingAfter: 4 });
      return;
    }

    data.references.forEach((item) => {
      const contact = item.email || item.phone;
      writeWrapped(`${item.name}${contact ? ` - ${contact}` : ''}`, { spacingAfter: 0 });
      const positionCompany = [item.position, item.company].filter(Boolean).join(', ').toUpperCase();
      if (positionCompany) writeWrapped(positionCompany, { style: 'bold', spacingAfter: 5 });
    });
  };

  try {
    addHeader();

    if (templateId === 'executive') {
      addSummary('OBJECTIVE');
      addExperience(true);
      addEducation();
      addSkills();
      addProjects();
      addPublications();
      addAdditionalInfo();
      addReferences(true);
    } else if (templateId === 'ats-optimized') {
      addSummary('SUMMARY');
      addExperience();
      addEducation();
      addSkills();
      addProjects();
      addPublications();
      addAdditionalInfo();
      addReferences(true);
    } else {
      addSummary('SUMMARY');
      addExperience();
      addEducation();
      addSkills();
      addProjects();
      addPublications();
      addAdditionalInfo();
      addReferences(true);
    }

    doc.save(getFileName(resumeData, 'pdf'));
  } catch (err) {
    console.error('PDF generation failed:', err);
    // Fallback: create a minimal valid PDF with basic info
    const fallbackDoc = new JsPDFConstructor({ unit: 'pt', format: 'letter' });
    fallbackDoc.setFont('courier', 'normal', 10);
    fallbackDoc.text('Resume Generation Error', 40, 40);
    fallbackDoc.text(
      'The PDF could not be generated due to encoding issues. ' +
      'Please try re-uploading your resume or manually entering the information.',
      40, 60, { maxWidth: 500 }
    );
    fallbackDoc.save(getFileName(resumeData, 'pdf'));
    throw err;
  }
}
